// User Profile Service Implementation
import { getConfig } from '../../config/index.js';
import { IUserProfileService } from '../interfaces/IUserProfileService.js';
import { SolanaService } from '../solanaService.js';
import { Connection } from '@solana/web3.js';
import { secureStorage } from '../../utils/security';

/**
 * Concrete implementation of IUserProfileService
 * Handles user profiles, onboarding, and transaction tracking
 */
export class UserProfileService extends IUserProfileService {
  constructor() {
    super();
    this.config = getConfig();
    this.storageKey = 'znoospace_user_profiles';
    this.transactionKey = 'znoospace_profile_transactions';
    
    // Initialize blockchain connection
    this.connection = new Connection('https://api.devnet.solana.com');
    this.solanaService = null;
    this.currentWallet = null;
  }

  /**
   * Initialize with wallet for blockchain operations
   * @param {any} wallet - Wallet adapter
   */
  initializeWithWallet(wallet) {
    this.currentWallet = wallet;
    if (wallet) {
      this.solanaService = new SolanaService(this.connection, wallet);
      if (this.config.ENABLE_DEBUG_LOGS) {
        console.log('👤 UserProfileService initialized with wallet:', wallet.publicKey?.toString());
      }
    }
  }

  /**
   * Get stored profiles from localStorage
   * @private
   */
  getStoredProfiles() {
    try {
      const stored = secureStorage.getItem(this.storageKey);
      if (!stored) return {};
      return typeof stored === 'string' ? JSON.parse(stored) : stored;
    } catch (error) {
      console.error('Error reading stored profiles:', error);
      return {};
    }
  }

  /**
   * Save profiles to localStorage
   * @private
   */
  saveStoredProfiles(profiles) {
    try {
      secureStorage.setItem(this.storageKey, JSON.stringify(profiles));
      return true;
    } catch (error) {
      console.error('Error saving profiles:', error);
      return false;
    }
  }

  /**
   * Get stored transactions from localStorage
   * @private
   */
  getStoredTransactions() {
    try {
      const stored = secureStorage.getItem(this.transactionKey);
      if (!stored) return {};
      return typeof stored === 'string' ? JSON.parse(stored) : stored;
    } catch (error) {
      console.error('Error reading stored transactions:', error);
      return {};
    }
  }

  /**
   * Save transactions to localStorage
   * @private
   */
  saveStoredTransactions(transactions) {
    try {
      secureStorage.setItem(this.transactionKey, JSON.stringify(transactions));
      return true;
    } catch (error) {
      console.error('Error saving transactions:', error);
      return false;
    }
  }

  /**
   * Generate a mock transaction ID for development
   * @private
   */
  generateMockTxId() {
    return `znoospace_profile_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Check if a wallet address has an existing profile
   * @param {string} walletAddress - User's wallet address
   * @returns {Promise<{exists: boolean, profile?: any, error?: string}>}
   */
  async checkProfileExists(walletAddress) {
    try {
      if (!walletAddress) {
        return { exists: false, error: 'Wallet address is required' };
      }

      if (this.config.ENABLE_DEBUG_LOGS) {
        console.log(`🔍 Checking profile for ${walletAddress} on blockchain...`);
      }

      // PRIORITY 1: Check existing profile index first to avoid rate limiting
      const storedProfiles = this.getStoredProfiles();
      console.log(`🔍 Cache check: Found ${Object.keys(storedProfiles).length} profiles in index`);
      const cachedProfile = storedProfiles[walletAddress];
      
      if (cachedProfile) {
        console.log(`💾 Found cached profile for ${walletAddress}:`, cachedProfile);
        // Treat as completed if status is 'completed' OR if profile has a username (fallback-created profiles may lack status)
        if (cachedProfile.status === 'completed' || cachedProfile.username) {
          // Sanitize displayName in cached profile (may be corrupted from earlier writes)
          let dn = cachedProfile.displayName;
          if (dn && typeof dn === 'object') {
            dn = dn.displayName || dn.name || dn.username || null;
          }
          if (!dn || dn === '[object Object]') {
            cachedProfile.displayName = cachedProfile.username || `User ${walletAddress.slice(0, 8)}`;
            // Re-save the fixed profile
            const profiles = this.getStoredProfiles();
            profiles[walletAddress] = cachedProfile;
            this.saveStoredProfiles(profiles);
          }
          console.log(`💾 Using indexed profile for ${walletAddress}:`, cachedProfile.displayName || cachedProfile.username);
          return {
            exists: true,
            profile: cachedProfile
          };
        } else {
          console.log(`⚠️ Cached profile status is '${cachedProfile.status}', not 'completed'`);
        }
      } else {
        console.log(`⚠️ No cached profile found for ${walletAddress}`);
      }

      // PRIORITY 2: Try to get profile from blockchain using memo transaction approach
      try {
        const blockchainProfile = await this.checkBlockchainProfileViaMemo(walletAddress);
        
        if (blockchainProfile) {
          if (this.config.ENABLE_DEBUG_LOGS) {
            console.log('✅ Profile found on blockchain via memo transactions:', blockchainProfile);
          }
          
          // Cache the profile locally for faster future access
          const profiles = this.getStoredProfiles();
          profiles[walletAddress] = {
            ...blockchainProfile,
            walletAddress,
            status: 'completed',
            lastUpdated: new Date().toISOString()
          };
          this.saveStoredProfiles(profiles);
          
          return {
            exists: true,
            profile: blockchainProfile
          };
        } else {
          if (this.config.ENABLE_DEBUG_LOGS) {
            console.log('ℹ️ Profile not found on blockchain for:', walletAddress);
          }
        }
      } catch (blockchainError) {
        console.error('❌ Blockchain profile check failed:', blockchainError.message);
        
        // If blockchain check fails, fall back to localStorage as cache
        const profiles = this.getStoredProfiles();
        const cachedProfile = profiles[walletAddress];
        
        if (cachedProfile && (cachedProfile.status === 'completed' || cachedProfile.username)) {
          console.log('⚠️ Using cached profile due to blockchain connectivity issues');
          return {
            exists: true,
            profile: cachedProfile,
            warning: 'Using cached data - blockchain connectivity issues'
          };
        }
      }

      // If no wallet adapter available, check localStorage as fallback
      const profiles = this.getStoredProfiles();
      const profile = profiles[walletAddress];

      if (this.config.ENABLE_DEBUG_LOGS) {
        console.log(`🔍 Fallback localStorage check for ${walletAddress}:`, profile ? 'EXISTS' : 'NOT FOUND');
      }

      return {
        exists: !!profile && (profile.status === 'completed' || !!profile.username),
        profile: profile || null
      };

    } catch (error) {
      console.error('Error checking profile existence:', error);
      return { exists: false, error: error.message };
    }
  }

  /**
   * Check for blockchain profile using memo transaction approach (like admin panel)
   * @param {string} walletAddress - Wallet address to check
   * @returns {Promise<Object|null>} Profile data if found, null otherwise
   */
  async checkBlockchainProfileViaMemo(walletAddress) {
    try {
      console.log(`🔍 Checking blockchain for profile via memo transactions: ${walletAddress}`);
      
      // Check cache first to avoid repeated blockchain calls
      const cacheKey = `profile_cache_${walletAddress}`;
      const cachedProfile = secureStorage.getItem(cacheKey);
      if (cachedProfile) {
        const cached = typeof cachedProfile === 'string' ? JSON.parse(cachedProfile) : cachedProfile;
        const cacheAge = Date.now() - cached.timestamp;
        // Use cache for 10 minutes to reduce blockchain calls
        if (cacheAge < 10 * 60 * 1000) {
          console.log(`💾 Using cached blockchain profile for ${walletAddress}:`, cached.profile);
          return cached.profile;
        }
      }
      
      // Import Solana Web3.js for direct blockchain queries
      const { Connection, PublicKey } = await import('@solana/web3.js');
      const connection = new Connection('https://api.devnet.solana.com', 'confirmed');
      
      // Fetch recent transactions — limit to 15 to avoid RPC rate limits on devnet
      const publicKey = new PublicKey(walletAddress);
      const signatures = await connection.getSignaturesForAddress(publicKey, { limit: 15 });
      
      console.log(`📋 Found ${signatures.length} transactions for wallet ${walletAddress}`);
      
      // Filter to only memo-program transactions first (via memo field in signature info)
      // Then fetch full transaction details only for candidates
      for (let i = 0; i < signatures.length; i++) {
        const sigInfo = signatures[i];
        
        // Quick skip: if the signature info has a memo field, check it first
        if (sigInfo.memo && !sigInfo.memo.includes('Profile:')) {
          continue; // Not a profile memo, skip without RPC call
        }
        
        try {
          // Rate limit: 1 RPC call per 300ms to stay well under devnet limits
          if (i > 0) {
            await new Promise(resolve => setTimeout(resolve, 300));
          }
          
          const transaction = await connection.getTransaction(sigInfo.signature, {
            commitment: 'confirmed',
            maxSupportedTransactionVersion: 0
          });
          
          if (transaction && transaction.meta && transaction.meta.logMessages) {
            // Look for profile memo logs (same pattern as admin panel)
            const profileLogs = transaction.meta.logMessages.filter(log => 
              log.includes('Znoospace Profile:') || log.includes('ChirpX Profile:')
            );
            
            if (profileLogs.length > 0) {
              const profileLog = profileLogs[0];
              const profileMatch = profileLog.match(/(?:Znoospace|ChirpX) Profile: (.+)/);
              
              if (profileMatch && profileMatch[1]) {
                try {
                  const rawJson = profileMatch[1].trim();
                  
                  // Enhanced JSON cleaning for blockchain data
                  let cleanJson = this.cleanBlockchainJSON(rawJson);
                  
                  // Additional cleaning for common blockchain JSON issues
                  cleanJson = cleanJson.replace(/}\s*{/g, '}'); // Remove duplicate objects
                  cleanJson = cleanJson.replace(/,\s*}/g, '}'); // Remove trailing commas
                  cleanJson = cleanJson.replace(/,\s*]/g, ']'); // Remove trailing commas in arrays
                  
                  // Try to find the first complete JSON object
                  const jsonStart = cleanJson.indexOf('{');
                  const jsonEnd = cleanJson.lastIndexOf('}');
                  if (jsonStart !== -1 && jsonEnd !== -1 && jsonEnd > jsonStart) {
                    cleanJson = cleanJson.substring(jsonStart, jsonEnd + 1);
                  }
                  
                  const profileData = JSON.parse(cleanJson);
                  
                  if (profileData && profileData.author === walletAddress) {
                    console.log(`✅ Found blockchain profile for ${walletAddress}:`, profileData);
                    
                    // Sanitize displayName — it may be an object or '[object Object]' from a corrupted write
                    let rawDisplayName = profileData.displayName;
                    if (rawDisplayName && typeof rawDisplayName === 'object') {
                      rawDisplayName = rawDisplayName.displayName || rawDisplayName.name || rawDisplayName.username || null;
                    }
                    if (!rawDisplayName || rawDisplayName === '[object Object]') {
                      rawDisplayName = null;
                    }
                    
                    const profile = {
                      username: profileData.username || `user_${walletAddress.slice(0, 8)}`,
                      displayName: rawDisplayName || profileData.username || `User ${walletAddress.slice(0, 8)}`,
                      bio: profileData.bio || '',
                      avatarUrl: profileData.avatarUrl || '',
                      author: profileData.author,
                      timestamp: profileData.timestamp,
                      transactionSignature: sigInfo.signature,
                      explorerUrl: `https://explorer.solana.com/tx/${sigInfo.signature}?cluster=devnet`
                    };
                    
                    // Cache the found profile to avoid repeated blockchain calls
                    secureStorage.setItem(cacheKey, JSON.stringify({
                      profile,
                      timestamp: Date.now()
                    }));
                    
                    return profile;
                  }
                } catch (parseError) {
                  // Only log first few parse errors to avoid spam
                  if (i < 3) {
                    console.log('Failed to parse profile data:', parseError.message);
                  }
                }
              }
            }
          }
        } catch (txError) {
          // Only log first few transaction errors to avoid spam
          if (i < 3) {
            console.log('Error checking transaction:', txError.message);
          }
          
          // If we hit rate limits, stop scanning — return null and let fallback handle it
          if (txError.message && txError.message.includes('429')) {
            console.log('⚠️ Rate limited by RPC, stopping blockchain scan');
            break;
          }
        }
      }
      
      console.log(`❌ No blockchain profile found for ${walletAddress}`);
      return null;
      
    } catch (error) {
      console.error('Error checking blockchain profile via memo:', error);
      return null;
    }
  }
  
  /**
   * Clean blockchain JSON data (same method as admin panel)
   * @param {string} rawJson - Raw JSON string from blockchain
   * @returns {string} Cleaned JSON string
   */
  cleanBlockchainJSON(rawJson) {
    return rawJson
      .replace(/[\x00-\x1F\x7F-\x9F]/g, '') // Remove control characters
      .replace(/\\n/g, ' ') // Replace escaped newlines
      .replace(/\\t/g, ' ') // Replace escaped tabs
      .replace(/\\r/g, ' ') // Replace escaped carriage returns
      .replace(/\\"/g, '"') // Unescape quotes
      .replace(/\\\\/g, '\\') // Unescape backslashes
      .trim();
  }

  /**
   * Create a new user profile on-chain using Solana smart contract
   * @param {string} walletAddress - User's wallet address
   * @param {string} username - Chosen username
   * @param {string} bio - User bio
   * @param {string} avatarUrl - Avatar image URL
   * @returns {Promise<{success: boolean, txId?: string, profile?: any, error?: string}>}
   */
  async createProfile(walletAddress, username, bio = '', avatarUrl = '') {
    try {
      if (!walletAddress || !username) {
        return { success: false, error: 'Wallet address and username are required' };
      }

      // Enforce username uniqueness before writing to blockchain
      const usernameCheck = await this.checkUsernameAvailability(username);
      if (!usernameCheck.available) {
        return { success: false, error: 'Username is already taken. Please choose a different username.' };
      }

      // Get wallet adapter for blockchain operations
      const wallet = await this.getWalletAdapter(walletAddress);
      if (!wallet) {
        return { success: false, error: 'Wallet not available for blockchain operations' };
      }

      // Initialize Solana service with wallet
      this.initializeWithWallet(wallet);

      if (!this.solanaService) {
        return { success: false, error: 'Failed to initialize blockchain service' };
      }

      if (this.config.ENABLE_DEBUG_LOGS) {
        console.log(`🔗 Creating profile on blockchain for ${walletAddress}:`, { username, bio, avatarUrl });
      }

      // Create profile on blockchain using SolanaService
      // Note: SolanaService expects displayName as first parameter, so we pass username as displayName
      const result = await this.solanaService.createUserProfile(String(username), String(bio), String(avatarUrl));

      if (result.success) {
        const profile = {
          walletAddress,
          username: result.profile.username,
          displayName: result.profile.displayName,
          bio: result.profile.bio,
          avatarUrl: result.profile.avatarUrl,
          createdAt: new Date().toISOString(),
          txId: result.signature,
          status: 'completed'
        };

        // Also save to localStorage as backup/cache
        const profiles = this.getStoredProfiles();
        profiles[walletAddress] = profile;
        this.saveStoredProfiles(profiles);

        // Save transaction record
        await this.saveProfileTransaction(walletAddress, result.signature, profile);

        if (this.config.ENABLE_DEBUG_LOGS) {
          console.log(`✅ Created profile on blockchain for ${walletAddress}:`, profile);
          console.log(`🔗 Transaction: ${result.explorerUrl}`);
        }

        return {
          success: true,
          txId: result.signature,
          profile,
          explorerUrl: result.explorerUrl
        };
      } else {
        return { success: false, error: result.error || 'Failed to create profile on blockchain' };
      }

    } catch (error) {
      console.error('❌ Error creating profile on blockchain:', error);
      
      // FALLBACK: Create profile locally when blockchain fails (for development/reliability)
      console.log('🔄 Blockchain failed, creating profile locally as fallback...');
      
      try {
        const fallbackProfile = {
          username,
          displayName: username, // Use username as displayName for fallback
          bio: bio || '',
          avatarUrl: avatarUrl || '',
          walletAddress,
          createdAt: new Date().toISOString(),
          source: 'local_fallback'
        };
        
        // Store profile locally with secure storage
        const profileKey = `profile_${walletAddress}`;
        await secureStorage.setItem(profileKey, JSON.stringify(fallbackProfile));
        
        // Also store in profile index for quick lookup
        const profiles = this.getStoredProfiles();
        profiles[walletAddress] = {
          ...fallbackProfile,
          lastUpdated: Date.now()
        };
        this.saveStoredProfiles(profiles);
        
        console.log('✅ Profile created locally as fallback');
        
        return {
          success: true,
          txId: `local_fallback_${Date.now()}`,
          profile: fallbackProfile,
          explorerUrl: null,
          fallback: true
        };
        
      } catch (fallbackError) {
        console.error('❌ Fallback profile creation also failed:', fallbackError);
        return { 
          success: false, 
          error: `Profile creation failed: ${error.message}. Fallback also failed: ${fallbackError.message}` 
        };
      }
    }
  }

  /**
   * Get wallet adapter for blockchain operations
   * @param {string} walletAddress - Wallet address to find adapter for
   * @returns {Promise<any>} Wallet adapter or null
   */
  async getWalletAdapter(walletAddress) {
    try {
      // Check for different wallet types
      if (window.nightly && window.nightly.solana) {
        const provider = window.nightly.solana;
        if (provider.publicKey && provider.publicKey.toString() === walletAddress) {
          return {
            publicKey: provider.publicKey,
            connected: provider.isConnected,
            signTransaction: provider.signTransaction?.bind(provider),
            signAllTransactions: provider.signAllTransactions?.bind(provider),
            walletName: 'Nightly'
          };
        }
      }
      
      if (window.solana && window.solana.isPhantom) {
        const provider = window.solana;
        if (provider.publicKey && provider.publicKey.toString() === walletAddress) {
          return {
            publicKey: provider.publicKey,
            connected: provider.isConnected,
            signTransaction: provider.signTransaction?.bind(provider),
            signAllTransactions: provider.signAllTransactions?.bind(provider),
            walletName: 'Phantom'
          };
        }
      }
      
      return null;
    } catch (error) {
      console.error('Error getting wallet adapter:', error);
      return null;
    }
  }

  /**
   * Fallback profile creation using localStorage
   * @param {string} walletAddress - User's wallet address
   * @param {string} username - Chosen username
   * @param {string} bio - User bio
   * @param {string} avatarUrl - Avatar image URL
   * @param {string} blockchainError - Original blockchain error
   * @returns {Promise<{success: boolean, txId?: string, profile?: any, error?: string}>}
   */
  async createProfileFallback(walletAddress, username, bio = '', avatarUrl = '', blockchainError = '') {
    try {
      // Check if username is available
      const usernameCheck = await this.checkUsernameAvailability(username);
      if (!usernameCheck.available) {
        return { success: false, error: 'Username is already taken' };
      }

      // Generate mock transaction ID
      const txId = this.generateMockTxId();

      const profile = {
        walletAddress,
        username,
        bio,
        avatarUrl,
        createdAt: new Date().toISOString(),
        txId,
        status: 'completed',
        fallback: true,
        blockchainError
      };

      // Save profile to localStorage
      const profiles = this.getStoredProfiles();
      profiles[walletAddress] = profile;
      this.saveStoredProfiles(profiles);

      // Save transaction record
      await this.saveProfileTransaction(walletAddress, txId, profile);

      if (this.config.ENABLE_DEBUG_LOGS) {
        console.log(`✅ Created fallback profile for ${walletAddress}:`, profile);
      }

      return {
        success: true,
        txId,
        profile,
        fallback: true
      };

    } catch (error) {
      console.error('Error creating fallback profile:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get user profile by wallet address
   * @param {string} walletAddress - User's wallet address
   * @returns {Promise<{success: boolean, profile?: any, error?: string}>}
   */
  async getProfile(walletAddress) {
    try {
      if (!walletAddress) {
        return { success: false, error: 'Wallet address is required' };
      }
      
      // First, try to get profile from blockchain
      const wallet = await this.getWalletAdapter(walletAddress);
      if (wallet && this.solanaService) {
        try {
          const blockchainResult = await this.solanaService.getUserProfile(wallet.publicKey);
          if (blockchainResult.success) {
            const profile = {
              walletAddress,
              username: blockchainResult.data.username,
              displayName: blockchainResult.data.displayName,
              bio: blockchainResult.data.bio,
              avatarUrl: blockchainResult.data.avatarUrl,
              createdAt: new Date(blockchainResult.data.createdAt * 1000).toISOString(),
              status: 'completed',
              onChain: true
            };
            
            // Cache in localStorage
            const profiles = this.getStoredProfiles();
            profiles[walletAddress] = profile;
            this.saveStoredProfiles(profiles);
            
            return { success: true, profile };
          }
        } catch (blockchainError) {
          console.error('❌ Blockchain profile fetch failed:', blockchainError.message);
          return { success: false, error: `Failed to load profile from blockchain: ${blockchainError.message}` };
        }
      }
      
      // NO FALLBACK - Profile must exist on blockchain
      return { success: false, error: 'Profile not found on blockchain. Please create a profile first.' };
    } catch (error) {
      console.error('Error getting profile:', error);
      return { success: false, error: error.message };
    }
  }
  /**
   * Update existing user profile
   * @param {string} walletAddress - User's wallet address
   * @param {Object} updates - Profile updates
   * @returns {Promise<{success: boolean, txId?: string, profile?: any, error?: string}>}
   */
  async updateProfile(walletAddress, updates) {
    try {
      const profileResult = await this.getProfile(walletAddress);
      if (!profileResult.success) {
        return { success: false, error: 'Profile not found' };
      }

      const txId = this.generateMockTxId();
      const updatedProfile = {
        ...profileResult.profile,
        ...updates,
        updatedAt: new Date().toISOString(),
        lastUpdateTxId: txId
      };

      const profiles = this.getStoredProfiles();
      profiles[walletAddress] = updatedProfile;
      this.saveStoredProfiles(profiles);

      if (this.config.ENABLE_DEBUG_LOGS) {
        console.log(`🔄 Updated profile for ${walletAddress}:`, updatedProfile);
      }

      return {
        success: true,
        txId,
        profile: updatedProfile
      };

    } catch (error) {
      console.error('Error updating profile:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Save profile creation transaction ID for tracking
   * @param {string} walletAddress - User's wallet address
   * @param {string} txId - Transaction ID
   * @param {Object} profileData - Profile data
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async saveProfileTransaction(walletAddress, txId, profileData) {
    try {
      const transactions = this.getStoredTransactions();
      transactions[walletAddress] = {
        txId,
        profileData,
        timestamp: new Date().toISOString(),
        status: 'completed' // In production: track actual transaction status
      };

      this.saveStoredTransactions(transactions);

      if (this.config.ENABLE_DEBUG_LOGS) {
        console.log(`💾 Saved transaction for ${walletAddress}: ${txId}`);
      }

      return { success: true };

    } catch (error) {
      console.error('Error saving profile transaction:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get profile creation transaction status
   * @param {string} walletAddress - User's wallet address
   * @returns {Promise<{success: boolean, txId?: string, status?: string, error?: string}>}
   */
  async getProfileTransactionStatus(walletAddress) {
    try {
      const transactions = this.getStoredTransactions();
      const transaction = transactions[walletAddress];

      if (!transaction) {
        return { success: false, error: 'No transaction found' };
      }

      return {
        success: true,
        txId: transaction.txId,
        status: transaction.status
      };

    } catch (error) {
      console.error('Error getting transaction status:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Check if username is available
   * @param {string} username - Username to check
   * @param {object} options - Options
   * @param {boolean} options.quickOnly - If true, only run fast local checks (Layers 1-2), skip network calls
   * @returns {Promise<{available: boolean, error?: string}>}
   */
  async checkUsernameAvailability(username, options = {}) {
    try {
      if (!username || username.length < 3) {
        return { available: false, error: 'Username must be at least 3 characters' };
      }

      const lowerUsername = username.toLowerCase();

      // Layer 1: Check localStorage profile index (fast, covers all previously seen profiles)
      const profiles = this.getStoredProfiles();
      const isUsernameUsedLocally = Object.values(profiles).some(
        profile => profile.username && profile.username.toLowerCase() === lowerUsername
      );
      if (isUsernameUsedLocally) {
        return { available: false };
      }

      // Layer 2: Check all known wallets' cached profile data in secureStorage
      // Note: cached profiles may nest username inside .profile or at top level
      try {
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (!key) continue;
          if (key.startsWith('profile_') || key.startsWith('profile_cache_') || key.startsWith('znoospace_onboarding')) {
            try {
              const raw = localStorage.getItem(key);
              if (raw) {
                const cached = typeof raw === 'string' ? JSON.parse(raw) : raw;
                // Check top-level username
                if (cached && cached.username && cached.username.toLowerCase() === lowerUsername) {
                  return { available: false };
                }
                // Check nested .profile.username (onboarding state & profile caches)
                if (cached && cached.profile && cached.profile.username && cached.profile.username.toLowerCase() === lowerUsername) {
                  return { available: false };
                }
              }
            } catch {}
          }
        }
      } catch {}

      // For real-time typing checks, local layers are sufficient — skip expensive network calls
      if (options.quickOnly) {
        return { available: true };
      }

      // Layer 3: Scan known wallets' blockchain profiles via Helius (if available)
      try {
        const { getHeliusService } = await import('../heliusService.js');
        const helius = getHeliusService();
        if (helius && helius.isAvailable()) {
          const knownRaw = localStorage.getItem('znoospace_known_wallets');
          if (knownRaw) {
            const knownWallets = JSON.parse(knownRaw);
            if (Array.isArray(knownWallets)) {
              // Query wallets in batches to avoid rate limits (check up to 20 wallets)
              const walletsToCheck = knownWallets.slice(0, 20);
              const results = await Promise.allSettled(
                walletsToCheck.map(addr =>
                  helius.getTransactions(addr, { limit: 10 }).catch(() => [])
                )
              );
              for (const result of results) {
                if (result.status !== 'fulfilled' || !Array.isArray(result.value)) continue;
                for (const tx of result.value) {
                  try {
                    const instructions = tx.instructions || [];
                    for (const inst of instructions) {
                      if (inst.programId === 'MemoSq4gqABAXKb96qnH8TysNcWxMyWCqXgDLGmfcHr' && inst.data) {
                        // Try base58 decode
                        let decoded = inst.data;
                        if (!decoded.includes('Profile:')) {
                          try {
                            const bs58 = (await import('bs58')).default;
                            decoded = Buffer.from(bs58.decode(inst.data)).toString('utf8');
                          } catch {}
                        }
                        if (decoded.includes('Profile:')) {
                          const match = decoded.match(/(?:Znoospace|ChirpX) Profile: (.+)/);
                          if (match) {
                            try {
                              const profileData = JSON.parse(match[1]);
                              if (profileData.username && profileData.username.toLowerCase() === lowerUsername) {
                                console.log(`🚫 Username "${username}" already taken on-chain by ${profileData.author || 'unknown'}`);
                                return { available: false };
                              }
                            } catch {}
                          }
                        }
                      }
                    }
                  } catch {}
                }
              }
            }
          }
        }
      } catch (e) {
        // Helius scan failed — continue to Layer 4
        console.warn('⚠️ Helius username scan failed, trying global RPC scan:', e.message);
      }

      // Layer 4: Global Memo program scan — catches usernames from ANY wallet,
      // even ones not in the local known_wallets registry.
      // Scans recent Memo program transactions for profile creation memos.
      try {
        const { Connection, PublicKey: PK } = await import('@solana/web3.js');
        const conn = new Connection('https://api.devnet.solana.com', 'confirmed');
        const MEMO_PROGRAM = new PK('MemoSq4gqABAXKb96qnH8TysNcWxMyWCqXgDLGmfcHr');
        
        // Fetch recent memo program signatures and filter for profile memos
        const globalSigs = await Promise.race([
          conn.getSignaturesForAddress(MEMO_PROGRAM, { limit: 200 }),
          new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 10000))
        ]);
        
        // Pre-filter: only fetch transactions whose memo field mentions "Profile:"
        const profileSigs = globalSigs.filter(sig =>
          sig.memo && (sig.memo.includes('Znoospace Profile') || sig.memo.includes('ChirpX Profile'))
        );
        
        console.log(`🔍 Username check Layer 4: scanning ${profileSigs.length} profile transactions globally`);
        
        // Check each profile transaction for the target username
        for (const sig of profileSigs.slice(0, 30)) {
          try {
            const tx = await Promise.race([
              conn.getTransaction(sig.signature, { maxSupportedTransactionVersion: 0 }),
              new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 3000))
            ]);
            
            if (!tx?.meta?.logMessages) continue;
            
            const profileLogs = tx.meta.logMessages.filter(log =>
              log.includes('Znoospace Profile:') || log.includes('ChirpX Profile:')
            );
            
            for (const log of profileLogs) {
              const match = log.match(/(?:Znoospace|ChirpX) Profile: (.+)/);
              if (match) {
                try {
                  let jsonStr = match[1].trim();
                  // Clean common blockchain JSON issues
                  if (jsonStr.startsWith('"') && jsonStr.endsWith('"')) jsonStr = jsonStr.slice(1, -1);
                  jsonStr = jsonStr.replace(/\\"/g, '"');
                  const profileData = JSON.parse(jsonStr);
                  if (profileData.username && profileData.username.toLowerCase() === lowerUsername) {
                    console.log(`🚫 Username "${username}" already taken on-chain by ${profileData.author || 'unknown'} (global scan)`);
                    return { available: false };
                  }
                } catch {}
              }
            }
          } catch {}
        }
      } catch (e) {
        console.warn('⚠️ Global memo scan failed:', e.message);
      }

      return { available: true };

    } catch (error) {
      console.error('Error checking username availability:', error);
      return { available: false, error: error.message };
    }
  }

  /**
   * Get user onboarding status
   * @param {string} walletAddress - User's wallet address
   * @returns {Promise<{status: 'new'|'pending'|'completed', profile?: any, txId?: string}>}
   */
  async getOnboardingStatus(walletAddress) {
    try {
      const profileCheck = await this.checkProfileExists(walletAddress);
      
      if (!profileCheck.exists) {
        return { status: 'new' };
      }

      const transactionStatus = await this.getProfileTransactionStatus(walletAddress);
      
      if (transactionStatus.success) {
        return {
          status: transactionStatus.status === 'completed' ? 'completed' : 'pending',
          profile: profileCheck.profile,
          txId: transactionStatus.txId
        };
      }

      return {
        status: 'completed',
        profile: profileCheck.profile
      };

    } catch (error) {
      console.error('Error getting onboarding status:', error);
      return { status: 'new' };
    }
  }

  /**
   * Clear all stored data (for testing/debugging)
   */
  clearAllData() {
    secureStorage.removeItem(this.storageKey);
    secureStorage.removeItem(this.transactionKey);
    console.log('🧹 Cleared all profile data');
  }
}
